/* Reusable PDPPL consent checkbox + full-text modal.
 *
 * Renders the mandatory Qatar Law No. 13 of 2016 acceptance gate used by:
 *   - Tenant signup modal (Toothmatrix.jsx)
 *   - Public Send-a-Case form (Workflow.jsx)
 *   - Admin Create-Client modal (Admin.jsx)
 *
 * The short text + version are fetched lazily from /api/legal/pdppl so the
 * canonical policy lives on the backend and every consent row records the
 * exact version the user saw.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { X, ShieldCheck, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function usePdpplPolicy() {
    const [policy, setPolicy] = useState(null);
    useEffect(() => {
        let alive = true;
        axios.get(`${API}/legal/pdppl`).then(({ data }) => {
            if (alive) setPolicy(data);
        }).catch(() => { /* leave null; checkbox label falls back to a default */ });
        return () => { alive = false; };
    }, []);
    return policy;
}

function PolicyModal({ policy, onClose }) {
    // Markdown is intentionally simple — backend ships plain text with
    // **bold** + bullet markers. We render paragraphs + headings without
    // a full parser to keep the bundle small.
    const blocks = (policy?.full_text_markdown || "").split(/\n\n+/);
    return (
        <div className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm flex items-start justify-center pt-[6vh] px-4 overflow-y-auto" data-testid="pdppl-modal">
            <div className="relative bg-white rounded-2xl w-full max-w-3xl p-8 shadow-2xl my-6">
                <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700" aria-label="Close"><X size={18} /></button>
                <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck size={18} className="text-cyan-700" />
                    <span className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500">· Qatar PDPPL · version {policy?.version}</span>
                </div>
                <h2 className="font-display text-2xl text-slate-900 tracking-tight mb-4">Personal Data Privacy — Acceptance Terms</h2>
                <div className="prose prose-slate max-w-none text-[14px] leading-relaxed space-y-4">
                    {blocks.map((b) => {
                        const trimmed = b.trim();
                        // The policy text is static + idempotent (server-owned). Use a short
                        // content-prefix as a stable, unique React key — avoids the array-index
                        // anti-pattern flagged by static analysis. Block content cannot collide
                        // because each paragraph in the source has a unique opening.
                        const blockKey = `pdppl-${trimmed.slice(0, 48)}`;
                        if (trimmed.startsWith("**") && trimmed.endsWith("**")) {
                            return <h3 key={blockKey} className="font-display text-[17px] text-slate-900 mt-5">{trimmed.replace(/\*\*/g, "")}</h3>;
                        }
                        if (trimmed.startsWith("- ")) {
                            const items = trimmed.split(/\n- /).map((x) => x.replace(/^- /, ""));
                            return (
                                <ul key={blockKey} className="list-disc pl-6 space-y-1.5 text-slate-700">
                                    {items.map((it) => <li key={`li-${it.slice(0, 64)}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(it.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>"), { ALLOWED_TAGS: ["strong"], ALLOWED_ATTR: [] }) }} />)}
                                </ul>
                            );
                        }
                        return <p key={blockKey} className="text-slate-700" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(trimmed.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>"), { ALLOWED_TAGS: ["strong"], ALLOWED_ATTR: [] }) }} />;
                    })}
                </div>
                <div className="mt-7 flex items-center justify-end gap-3">
                    <a
                        href="https://www.cdp.gov.qa/"
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1 text-[12.5px] text-slate-600 hover:text-slate-900"
                    >
                        Qatar CDP <ExternalLink size={11} />
                    </a>
                    <button onClick={onClose} data-testid="pdppl-modal-close" className="px-4 py-2 rounded-lg bg-slate-900 text-white text-[13px] font-medium hover:bg-slate-700">
                        I have read the full text
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function PrivacyConsentCheckbox({ value, onChange, testid = "consent-pdppl", compact = false }) {
    const policy = usePdpplPolicy();
    const [showFull, setShowFull] = useState(false);
    const shortText = policy?.short_text || "I agree to comply with Qatar Law No. 13 of 2016 (PDPPL). I take full responsibility for the personal data I process on this platform and acknowledge that Toothmatrix and its operators are not liable for any breach, misuse, or non-compliance arising from my use of the service.";
    const cardCls = value
        ? "border-emerald-300 dark:border-emerald-400/50 bg-emerald-50/40"
        : "border-cyan-300 dark:border-cyan-400/40 bg-cyan-50/40";
    const linkCls = "text-cyan-700 hover:text-cyan-900 dark:text-cyan-300 dark:hover:text-cyan-200 underline";

    return (
        <>
            <label
                className={`flex items-start gap-3 rounded-xl border ${cardCls} px-4 py-3 cursor-pointer transition`}
                data-testid={`${testid}-label`}
            >
                <input
                    type="checkbox"
                    data-testid={testid}
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-400 accent-cyan-600 text-cyan-600 focus:ring-cyan-500 shrink-0"
                />
                <span className="text-[12.5px] leading-snug text-slate-700">
                    <span className="font-mono-tr text-[10px] tracking-[0.22em] uppercase block mb-1 text-slate-500">
                        · Qatar PDPPL · mandatory · v{policy?.version || "1.0"}
                    </span>
                    {compact ? (
                        <span>
                            I agree to <strong>Qatar Law No. 13 of 2016 (PDPPL)</strong> and accept that Toothmatrix is <strong>not liable</strong> for my data-handling.{" "}
                            <button type="button" onClick={(e) => { e.preventDefault(); setShowFull(true); }} data-testid={`${testid}-read-full`} className={linkCls}>Read full text</button>
                        </span>
                    ) : (
                        <span>
                            {shortText}{" "}
                            <button type="button" onClick={(e) => { e.preventDefault(); setShowFull(true); }} data-testid={`${testid}-read-full`} className={`${linkCls} whitespace-nowrap`}>Read full text</button>
                        </span>
                    )}
                </span>
            </label>
            {showFull && policy && <PolicyModal policy={policy} onClose={() => setShowFull(false)} />}
        </>
    );
}
